import { ChevronsUpDownIcon } from 'lucide-react';
import { useState } from 'react';

import { AccessContext } from '@invenco/common-interface/auth';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/core/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/core/popover';
import { SidebarMenuButton, useSidebar } from '@/components/core/sidebar';
import { cn } from '@/shared/helpers';

import { ActiveMembershipDetail } from './active-membership-detail';
import { getMembershipIcon, getMembershipName } from './membership-helpers';
import { useMemberships } from './use-memberships';

const contextToGroupTitle = {
  [AccessContext.SELLER]: 'Accounts',
  [AccessContext.WAREHOUSE]: 'Warehouses',
  [AccessContext.PLATFORM]: 'Other',
  [AccessContext.SYSTEM]: 'System',
};

export function AccountMenu() {
  const {
    isLoading,
    activeMembership,
    changingToMembership,
    hasMultipleMemberships,
    membershipGroups,
    selectMembership,
  } = useMemberships();
  const { isMobile } = useSidebar();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  if (!hasMultipleMemberships) {
    return (
      <ActiveMembershipDetail membership={activeMembership} loading={isLoading} className="p-2" />
    );
  }

  const isShowingGroupHeadings = membershipGroups.length > 1;

  return (
    <Popover open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <PopoverTrigger asChild>
        <SidebarMenuButton
          isActive={isMenuOpen}
          role="combobox"
          aria-expanded={isMenuOpen}
          aria-label="Switch account"
        >
          <ActiveMembershipDetail
            membership={changingToMembership ?? activeMembership}
            loading={isLoading || !!changingToMembership}
          />
          <ChevronsUpDownIcon className="ml-auto size-4" />
        </SidebarMenuButton>
      </PopoverTrigger>
      <PopoverContent
        side={isMobile ? 'bottom' : 'right'}
        className={cn('p-0', isMobile && 'w-(--radix-popover-trigger-width)')}
        align={isMobile ? 'center' : 'start'}
      >
        <Command
          defaultValue={activeMembership?.id}
          // ignore value in search, instead use the keywords (generated from membership name)
          filter={(_, search, keywords) =>
            keywords?.some((keyword) => keyword.toLowerCase().includes(search.toLowerCase()))
              ? 1
              : 0
          }
        >
          <CommandInput placeholder="Search accounts..." />
          <CommandList>
            <CommandEmpty>No matching accounts</CommandEmpty>
            {membershipGroups.map((group) => (
              <CommandGroup
                key={group.context}
                heading={
                  isShowingGroupHeadings && group.context !== AccessContext.PLATFORM
                    ? contextToGroupTitle[group.context]
                    : undefined
                }
              >
                {group.memberships.map((membership) => {
                  const name = getMembershipName(membership);
                  return (
                    <CommandItem
                      key={membership.id}
                      value={membership.id}
                      data-checked={membership.id === activeMembership?.id ? 'true' : undefined}
                      keywords={name ? [name] : undefined}
                      onSelect={() => {
                        if (membership.id === activeMembership?.id) return;
                        void selectMembership({ membershipId: membership.id });
                        setIsMenuOpen(false);
                      }}
                    >
                      {getMembershipIcon(membership)}
                      {getMembershipName(membership)}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
